import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {changePassword} from '../../../api/memberApi';
import {defaultAlert} from "../../../utils/alert";
import {validatePassword} from "../../../utils/validation";

const ChangePasswordPage = () => {
    const navigate = useNavigate();

    const [form, setForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [validation, setValidation] = useState({
        currentPasswordChecked: false,
        newPasswordValid: false,
        confirmPasswordMatch: false
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setForm(prev => ({
            ...prev,
            [name]: value
        }));

        // 실시간 유효성 검사
        if (name === 'newPassword') {
            const isValid = validatePassword(value);
            setValidation(prev => ({
                ...prev,
                newPasswordValid: isValid
            }));

            if (value && !isValid) {
                setErrors(prev => ({
                    ...prev,
                    newPassword: '비밀번호는 영문 대소문자/숫자/특수문자 중 2가지 이상 조합, 8~20자여야 합니다.'
                }));
            } else if (isValid) {
                setErrors(prev => ({
                    ...prev,
                    newPassword: ''
                }));
            }

            // 새 비밀번호 변경 시 확인 비밀번호도 다시 체크
            if (form.confirmPassword) {
                const isMatch = value === form.confirmPassword;
                setValidation(prev => ({
                    ...prev,
                    confirmPasswordMatch: isMatch
                }));
                setErrors(prev => ({
                    ...prev,
                    confirmPassword: isMatch ? '' : '새 비밀번호가 일치하지 않습니다.'
                }));
            }
        }

        if (name === 'confirmPassword') {
            const isMatch = value === form.newPassword;
            setValidation(prev => ({
                ...prev,
                confirmPasswordMatch: isMatch
            }));

            if (value && !isMatch) {
                setErrors(prev => ({
                    ...prev,
                    confirmPassword: '새 비밀번호가 일치하지 않습니다.'
                }));
            } else if (isMatch) {
                setErrors(prev => ({
                    ...prev,
                    confirmPassword: ''
                }));
            }
        }
    };

    const handleSubmit = async () => {
        // 유효성 검사
        if (!form.currentPassword) {
            defaultAlert('현재 비밀번호를 입력해주세요.');
            return;
        }

        if (!form.newPassword) {
            defaultAlert('새 비밀번호를 입력해주세요.');
            return;
        }

        if (!validatePassword(form.newPassword)) {
            defaultAlert('비밀번호는 영문 대소문자/숫자/특수문자 중 2가지 이상 조합, 8~20자여야 합니다.');
            return;
        }

        if (!form.confirmPassword) {
            defaultAlert('새 비밀번호 확인을 입력해주세요.');
            return;
        }

        if (form.newPassword !== form.confirmPassword) {
            defaultAlert('새 비밀번호가 일치하지 않습니다.');
            return;
        }

        if (form.currentPassword === form.newPassword) {
            defaultAlert('현재 비밀번호와 새 비밀번호가 동일합니다.');
            return;
        }

        try {
            await changePassword(form.currentPassword, form.newPassword);
            defaultAlert('비밀번호가 성공적으로 변경되었습니다.');
            navigate('/menu/myInfo');
        } catch (error: any) {
            if (error.response?.status === 400) {
                setErrors(prev => ({
                    ...prev,
                    currentPassword: '현재 비밀번호가 일치하지 않습니다.'
                }));
                setValidation(prev => ({
                    ...prev,
                    currentPasswordChecked: false
                }));
            } else {
                defaultAlert('비밀번호 변경에 실패했습니다.');
            }
        }
    };

    return (
        <>
            <div className="bottom-btn">
                <button
                    type="button"
                    className="btn confirm"
                    onClick={handleSubmit}
                >
                    저장
                </button>
            </div>

            <main className="container">
                <section className="tit-box-wrap">
                    <p className="subtit">
                        현재 비밀번호가 일치하는 경우<br/>
                        새 비밀번호를 설정할 수 있습니다.
                    </p>
                </section>

                <form className="form-box">
                    <section className="bg-white">
                        <fieldset>
                            <legend>비밀번호 변경 폼</legend>
                            <div className="inner-box">
                                {/* 현재 비밀번호 */}
                                <div className="form-item">
                                    <label className="input-tit">현재 비밀번호</label>
                                    <div className="input-box">
                                        <input
                                            type="password"
                                            name="currentPassword"
                                            value={form.currentPassword}
                                            onChange={handleChange}
                                            placeholder="현재 비밀번호를 입력하세요"
                                        />
                                    </div>
                                    {errors.currentPassword && (
                                        <p className="guide_error">{errors.currentPassword}</p>
                                    )}
                                </div>

                                {/* 새 비밀번호 */}
                                <div className="form-item">
                                    <label className="input-tit">새 비밀번호</label>
                                    <div className="input-box">
                                        <input
                                            type="password"
                                            name="newPassword"
                                            value={form.newPassword}
                                            onChange={handleChange}
                                            placeholder="새 비밀번호를 입력하세요"
                                        />
                                    </div>
                                    {validation.newPasswordValid && !errors.newPassword && (
                                        <p className="guide_success">사용 가능한 비밀번호입니다.</p>
                                    )}
                                    {errors.newPassword && (
                                        <p className="guide_error">{errors.newPassword}</p>
                                    )}
                                </div>

                                {/* 새 비밀번호 확인 */}
                                <div className="form-item">
                                    <label className="input-tit">새 비밀번호 확인</label>
                                    <div className="input-box">
                                        <input
                                            type="password"
                                            name="confirmPassword"
                                            value={form.confirmPassword}
                                            onChange={handleChange}
                                            placeholder="새 비밀번호를 다시 입력하세요"
                                        />
                                    </div>
                                    {validation.confirmPasswordMatch && !errors.confirmPassword && (
                                        <p className="guide_success">새 비밀번호가 일치합니다.</p>
                                    )}
                                    {errors.confirmPassword && (
                                        <p className="guide_error">{errors.confirmPassword}</p>
                                    )}
                                </div>

                                {/* 안내 문구 */}
                                <div className="form-item">
                                    <ul className="guidF-list">
                                        <li>비밀번호는 영문 대소문자/숫자/특수문자 중 2가지 이상 조합, 8자~20자</li>
                                    </ul>
                                </div>
                            </div>
                        </fieldset>
                    </section>
                </form>
            </main>
        </>
    );
};

export default ChangePasswordPage;